import { createSlice } from '@reduxjs/toolkit'
import { createAsyncThunk } from '@reduxjs/toolkit'
import MaintenanceService from 'services/MaintenanceService'

export const initialState = {
    addMaintenanceRecord: {
        data: [],
        isLoading: false,
        error: false,
    },

    maintenanceRecordList: {
        data: [],
        isLoading: false,
        error: false,
    },
    maintenanceRecordDetail: {
        data: [],
        isLoading: false,
        error: false,
    },
    maintenanceRecordEdit: {
        data: [],
        isLoading: false,
        error: false,
    },
    maintenanceAttachmentDelete: {
        data: [],
        isLoading: false,
        error: false,
    },
    checklistTasks: {
        data: [],
        isLoading: false,
        error: false,
    },
    updateChecklistTasks: {
        data: [],
        isLoading: false,
        error: false,
    },
    maintenanceRecordChecklistId: {
        data: [],
        isLoading: false,
        error: false,
    },
    checklistTasksById: {
        data: [],
        isLoading: false,
        error: false,
    },
}

export const addMaintenanceRecord = createAsyncThunk(
    'maintenance/addMaintenanceRecord',
    async (data, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.addMaintenanceRecord(data)
            return [response]
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Error')
        }
    }
)

export const fetchMaintenanceRecordList = createAsyncThunk(
    'maintenance/getMaintenanceRecords',
    async (data, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getMaintenanceRecords(data)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const deleteMaintenanceRecord = createAsyncThunk(
    'maintenance/deleteMaintenanceRecords',
    async (params, { rejectWithValue }) => {
        try {
            await MaintenanceService.deleteMaintenanceRecord(params)
            return params
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Error')
        }
    }
)

export const deleteMaintenanceAttachment = createAsyncThunk(
    'maintenance/deleteMaintenanceAttachment',
    async (params, { rejectWithValue }) => {
        try {
            await MaintenanceService.deleteAttachment(params)
            return params
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Error')
        }
    }
)

export const fetchMaintenanceRecordDetail = createAsyncThunk(
    'maintenance/getMaintenanceRecordDetail',
    async (params, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getMaintenanceRecordDetail(params)
            return response.data
        } catch (err) {
            console.log(err)
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const updateMaintenanceRecord = createAsyncThunk(
    'maintenance/updateMaintenanceRecord',
    async (data, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.updateMaintenanceRecord(data)
            return response
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Error')
        }
    }
)

export const uploadAttachments = createAsyncThunk(
    'maintenance/uploadAttachments',
    async (data, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.uploadAttachments(data)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Error')
        }
    }
)

export const fetchRecordsToExcel = createAsyncThunk(
    'maintenance/getRecordsExcel',
    async (data, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getRecordsToExcel(data)
            return response
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const fetchChecklistTasks = createAsyncThunk(
    'maintenance/fetchChecklistTasks',
    async (maintenanceRecordId, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getChecklistTasks(maintenanceRecordId)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const updateChecklistTasks = createAsyncThunk(
    'maintenance/updateChecklistTasks',
    async (checklistData, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.updateChecklistTasks(checklistData)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const getMaintenanceRecordChecklistId = createAsyncThunk(
    'maintenance/getMaintenanceRecordChecklistId',
    async (maintenanceRecordId, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getMaintenanceRecordChecklistId(maintenanceRecordId)
            return response
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const getChecklistTasksById = createAsyncThunk(
    'maintenance/getChecklistTasksById',
    async (checklistId, { rejectWithValue }) => {
        try {
            const response = await MaintenanceService.getChecklistTasksById(checklistId)
            return response
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const maintenanceSlice = createSlice({
    name: 'maintenance',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(addMaintenanceRecord.pending, (state, action) => {
                state.addMaintenanceRecord.isLoading = true
            })
            .addCase(addMaintenanceRecord.rejected, (state, action) => {
                state.addMaintenanceRecord.isLoading = false
                state.addMaintenanceRecord.error = action.error.message
            })
            .addCase(addMaintenanceRecord.fulfilled, (state, action) => {
                state.addMaintenanceRecord.isLoading = false
                state.addMaintenanceRecord.error = false
                state.addMaintenanceRecord.success = true
                state.maintenanceRecordList.data = action.payload
            })

            .addCase(fetchMaintenanceRecordList.pending, (state, action) => {
                state.maintenanceRecordList.isLoading = true
            })
            .addCase(fetchMaintenanceRecordList.rejected, (state, action) => {
                state.maintenanceRecordList.isLoading = false
                state.maintenanceRecordList.error = action.error.message
            })
            .addCase(fetchMaintenanceRecordList.fulfilled, (state, action) => {
                state.maintenanceRecordList.isLoading = false
                state.maintenanceRecordList.error = false
                state.maintenanceRecordList.data = action.payload
            })

            // Record Detail
            .addCase(fetchMaintenanceRecordDetail.pending, (state, action) => {
                state.maintenanceRecordDetail.isLoading = true
            })
            .addCase(fetchMaintenanceRecordDetail.rejected, (state, action) => {
                state.maintenanceRecordDetail.error = action.error.message
            })
            .addCase(fetchMaintenanceRecordDetail.fulfilled, (state, action) => {
                state.maintenanceRecordDetail.isLoading = false
                state.maintenanceRecordDetail.error = false
                state.maintenanceRecordDetail.data = action.payload
            })

            // Update Record
            .addCase(updateMaintenanceRecord.pending, (state, action) => {
                state.maintenanceRecordEdit.isLoading = true
            })
            .addCase(updateMaintenanceRecord.rejected, (state, action) => {
                state.maintenanceRecordEdit.isLoading = false
                state.maintenanceRecordEdit.error = action.error.message
            })
            .addCase(updateMaintenanceRecord.fulfilled, (state, action) => {
                state.maintenanceRecordEdit.isLoading = false
                state.maintenanceRecordEdit.error = false
                state.maintenanceRecordEdit.success = true
            })

            .addCase(deleteMaintenanceAttachment.fulfilled, (state, action) => {
                state.maintenanceAttachmentDelete.isLoading = false
                state.maintenanceAttachmentDelete.error = false
                state.maintenanceAttachmentDelete.success = true
                const attachmentIdToRemove = action.payload
                if (state.maintenanceRecordDetail.data.attachments) {
                    state.maintenanceRecordDetail.data.attachments =
                        state.maintenanceRecordDetail.data.attachments.filter(
                            (attachment) => attachment.id !== attachmentIdToRemove
                        )
                }
            })

            // Checklist Tasks
            .addCase(fetchChecklistTasks.pending, (state, action) => {
                state.checklistTasks.isLoading = true
            })
            .addCase(fetchChecklistTasks.rejected, (state, action) => {
                state.checklistTasks.isLoading = false
                state.checklistTasks.error = action.error.message
            })
            .addCase(fetchChecklistTasks.fulfilled, (state, action) => {
                state.checklistTasks.isLoading = false
                state.checklistTasks.error = false
                state.checklistTasks.data = action.payload
            })

            // Update Checklist Tasks
            .addCase(updateChecklistTasks.pending, (state, action) => {
                state.updateChecklistTasks.isLoading = true
            })
            .addCase(updateChecklistTasks.rejected, (state, action) => {
                state.updateChecklistTasks.isLoading = false
                state.updateChecklistTasks.error = action.error.message
            })
            .addCase(updateChecklistTasks.fulfilled, (state, action) => {
                state.updateChecklistTasks.isLoading = false
                state.updateChecklistTasks.error = false
                state.updateChecklistTasks.data = action.payload
            })

            // Get Maintenance Record Checklist ID
            .addCase(getMaintenanceRecordChecklistId.pending, (state, action) => {
                state.maintenanceRecordChecklistId.isLoading = true
            })
            .addCase(getMaintenanceRecordChecklistId.rejected, (state, action) => {
                state.maintenanceRecordChecklistId.isLoading = false
                state.maintenanceRecordChecklistId.error = action.error.message
            })
            .addCase(getMaintenanceRecordChecklistId.fulfilled, (state, action) => {
                state.maintenanceRecordChecklistId.isLoading = false
                state.maintenanceRecordChecklistId.error = false
                state.maintenanceRecordChecklistId.data = action.payload
            })

            // Get Checklist Tasks By ID
            .addCase(getChecklistTasksById.pending, (state, action) => {
                state.checklistTasksById.isLoading = true
            })
            .addCase(getChecklistTasksById.rejected, (state, action) => {
                state.checklistTasksById.isLoading = false
                state.checklistTasksById.error = action.error.message
            })
            .addCase(getChecklistTasksById.fulfilled, (state, action) => {
                state.checklistTasksById.isLoading = false
                state.checklistTasksById.error = false
                state.checklistTasksById.data = action.payload
            })
    },
})

export default maintenanceSlice.reducer
